import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import {
  Cameraswitch,
  Close,
  FlashOff,
  FlashOn,
  Pause,
  PlayArrow,
  QrCodeScanner,
} from '@mui/icons-material';

const DUPLICATE_SCAN_WINDOW_MS = 900;
const SUCCESS_MESSAGE_MS = 1000;

const SCAN_FORMATS = [
  'aztec',
  'code_128',
  'code_39',
  'code_93',
  'codabar',
  'data_matrix',
  'ean_13',
  'ean_8',
  'itf',
  'pdf417',
  'qr_code',
  'upc_a',
  'upc_e',
];

export default function MobileScannerModal({ open, onClose, onDetected }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const detectorRef = useRef(null);
  const fallbackScannerRef = useRef(null);
  const fallbackControlsRef = useRef(null);
  const frameRef = useRef(null);
  const scanLockRef = useRef(false);
  const lastScanRef = useRef({ code: '', at: 0 });
  const successTimerRef = useRef(null);

  const [isPaused, setIsPaused] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [status, setStatus] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');
  const [isTorchEnabled, setIsTorchEnabled] = useState(false);
  const [supportsTorch, setSupportsTorch] = useState(false);
  const [cameraFacingMode, setCameraFacingMode] = useState('environment');
  const [scannerEngine, setScannerEngine] = useState('');

  const isBarcodeDetectorSupported = useMemo(
    () => typeof window !== 'undefined' && 'BarcodeDetector' in window,
    []
  );

  const stopScanner = async () => {
    if (successTimerRef.current) {
      clearTimeout(successTimerRef.current);
      successTimerRef.current = null;
    }

    if (frameRef.current) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }

    const fallbackScanner = fallbackScannerRef.current;
    const fallbackControls = fallbackControlsRef.current;
    if (fallbackControls) {
      try {
        fallbackControls.stop();
      } catch (_) {}
      fallbackControlsRef.current = null;
    }
    fallbackScannerRef.current = null;

    const stream = streamRef.current;
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    detectorRef.current = null;
  };

  const handleDetectedCode = async (rawCode) => {
    const nextCode = rawCode?.trim();
    if (!nextCode) return;

    const now = Date.now();
    const isDuplicate =
      lastScanRef.current.code === nextCode &&
      (now - lastScanRef.current.at) < DUPLICATE_SCAN_WINDOW_MS;

    if (isDuplicate || scanLockRef.current) return;

    scanLockRef.current = true;
    lastScanRef.current = { code: nextCode, at: now };

    try {
      const result = await onDetected(nextCode);
      setSuccessMessage(result?.message || `Scanned ${nextCode}`);
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
      successTimerRef.current = setTimeout(() => {
        setSuccessMessage('');
      }, SUCCESS_MESSAGE_MS);
    } finally {
      scanLockRef.current = false;
    }
  };

  const detectCodes = async () => {
    if (!open || isPaused || !videoRef.current || !detectorRef.current || scanLockRef.current) {
      frameRef.current = requestAnimationFrame(detectCodes);
      return;
    }

    try {
      const barcodes = await detectorRef.current.detect(videoRef.current);
      await handleDetectedCode(barcodes?.[0]?.rawValue);
    } catch (scanError) {
      if (!error) {
        setError(scanError?.message || 'Unable to read from camera.');
      }
    }

    frameRef.current = requestAnimationFrame(detectCodes);
  };

  useEffect(() => {
    if (!open) {
      stopScanner();
      setIsPaused(false);
      setStatus('');
      setSuccessMessage('');
      setError('');
      setIsTorchEnabled(false);
      setScannerEngine('');
      return undefined;
    }

    let isMounted = true;

    const startScanner = async () => {
      const startFallbackScanner = async () => {
        const {
          BrowserMultiFormatReader,
          BarcodeFormat,
        } = await import('@zxing/browser');
        const { DecodeHintType, NotFoundException } = await import('@zxing/library');
        if (!isMounted) return;

        const hints = new Map();
        hints.set(DecodeHintType.POSSIBLE_FORMATS, [
          BarcodeFormat.QR_CODE,
          BarcodeFormat.AZTEC,
          BarcodeFormat.CODABAR,
          BarcodeFormat.CODE_39,
          BarcodeFormat.CODE_93,
          BarcodeFormat.CODE_128,
          BarcodeFormat.DATA_MATRIX,
          BarcodeFormat.ITF,
          BarcodeFormat.EAN_13,
          BarcodeFormat.EAN_8,
          BarcodeFormat.PDF_417,
          BarcodeFormat.UPC_A,
          BarcodeFormat.UPC_E,
        ]);

        const scanner = new BrowserMultiFormatReader(hints, {
          delayBetweenScanAttempts: 80,
          delayBetweenScanSuccess: 250,
        });
        fallbackScannerRef.current = scanner;
        setSupportsTorch(false);
        setScannerEngine('zxing');

        const controls = await scanner.decodeFromConstraints(
          {
            audio: false,
            video: {
              facingMode: { ideal: cameraFacingMode },
            },
          },
          videoRef.current,
          (result, scanError) => {
            if (!isMounted || isPaused || scanLockRef.current) return;
            if (result) {
              void handleDetectedCode(result.getText());
              return;
            }
            if (scanError && !(scanError instanceof NotFoundException) && !error) {
              setError(scanError?.message || 'Unable to read from camera.');
            }
          }
        );
        fallbackControlsRef.current = controls;

        if (!isMounted) {
          try {
            controls.stop();
          } catch (_) {}
          return;
        }

        setStatus('Point the camera at a barcode or QR code.');
      };

      setIsStarting(true);
      setError('');
      setStatus('Opening camera…');

      try {
        if (isBarcodeDetectorSupported) {
          detectorRef.current = new window.BarcodeDetector({ formats: SCAN_FORMATS });
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: false,
            video: {
              facingMode: { ideal: cameraFacingMode },
            },
          });

          if (!isMounted) {
            stream.getTracks().forEach((track) => track.stop());
            return;
          }

          streamRef.current = stream;
          const videoTrack = stream.getVideoTracks()[0];
          const capabilities = typeof videoTrack?.getCapabilities === 'function' ? videoTrack.getCapabilities() : {};
          setSupportsTorch(Boolean(capabilities?.torch));
          setScannerEngine('native');

          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            await videoRef.current.play();
          }

          setStatus('Point the camera at a barcode or QR code.');
          frameRef.current = requestAnimationFrame(detectCodes);
          return;
        }

        await startFallbackScanner();
      } catch (cameraError) {
        const shouldTryFallback = isBarcodeDetectorSupported && detectorRef.current;

        if (shouldTryFallback) {
          try {
            await stopScanner();
            await startFallbackScanner();
            return;
          } catch (_) {}
        }

        setError(cameraError?.message || 'Camera access was denied.');
        setStatus('');
        setScannerEngine('');
      } finally {
        if (isMounted) setIsStarting(false);
      }
    };

    void startScanner();

    return () => {
      isMounted = false;
      void stopScanner();
    };
  }, [cameraFacingMode, isBarcodeDetectorSupported, onDetected, open]);

  useEffect(() => () => {
    if (successTimerRef.current) {
      clearTimeout(successTimerRef.current);
    }
  }, []);

  const handleToggleTorch = async () => {
    if (scannerEngine !== 'native') return;
    const track = streamRef.current?.getVideoTracks?.()[0];
    if (!track || typeof track.applyConstraints !== 'function') return;

    try {
      const nextTorchState = !isTorchEnabled;
      await track.applyConstraints({ advanced: [{ torch: nextTorchState }] });
      setIsTorchEnabled(nextTorchState);
    } catch (_) {}
  };

  return (
    <Dialog fullScreen open={open} onClose={onClose}>
      <Box sx={{ height: '100%', bgcolor: '#020617', color: '#fff', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <Box>
            <Typography fontSize="1rem" fontWeight={800}>Scan Products</Typography>
            <Typography fontSize="0.74rem" color="rgba(255,255,255,0.72)">
              Continuous scan mode. Each successful scan adds a product automatically.
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: '#fff' }}>
            <Close />
          </IconButton>
        </Box>

        <Box sx={{ flexGrow: 1, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(180deg, rgba(2,6,23,0.16) 0%, rgba(2,6,23,0.5) 100%)',
              pointerEvents: 'none',
            }}
          />
          <Box sx={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden' }}>
            <video
              ref={videoRef}
              muted
              playsInline
              autoPlay
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
              }}
            />
          </Box>
          <Box
            sx={{
              position: 'absolute',
              width: 'min(78vw, 360px)',
              height: 'min(46vw, 220px)',
              border: '2px solid rgba(255,255,255,0.92)',
              borderRadius: '18px',
              boxShadow: '0 0 0 9999px rgba(2,6,23,0.42)',
              pointerEvents: 'none',
            }}
          />
        </Box>

        <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid rgba(255,255,255,0.08)', bgcolor: 'rgba(2,6,23,0.92)' }}>
          <Stack spacing={1.25}>
            {error ? (
              <Alert severity="error">{error}</Alert>
            ) : successMessage ? (
              <Alert
                severity="success"
                sx={{ bgcolor: 'rgba(22,163,74,0.16)', color: '#fff', '& .MuiAlert-icon': { color: '#86EFAC' } }}
              >
                {successMessage}
              </Alert>
            ) : (
              <Alert
                icon={<QrCodeScanner fontSize="inherit" />}
                severity="info"
                sx={{ bgcolor: 'rgba(30,41,59,0.92)', color: '#fff', '& .MuiAlert-icon': { color: '#fff' } }}
              >
                {status || 'Point the camera at a barcode or QR code.'}
              </Alert>
            )}

            <Stack direction="row" spacing={1}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={isPaused ? <PlayArrow /> : <Pause />}
                onClick={() => setIsPaused((prev) => !prev)}
                disabled={isStarting || !!error}
                sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.18)' }}
              >
                {isPaused ? 'Resume' : 'Pause'}
              </Button>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Cameraswitch />}
                onClick={() => setCameraFacingMode((prev) => (prev === 'environment' ? 'user' : 'environment'))}
                disabled={isStarting}
                sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.18)' }}
              >
                Switch
              </Button>
              <Button
                fullWidth
                variant="outlined"
                startIcon={isTorchEnabled ? <FlashOff /> : <FlashOn />}
                onClick={handleToggleTorch}
                disabled={!supportsTorch || isStarting}
                sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.18)' }}
              >
                Torch
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Box>
    </Dialog>
  );
}
